import { ICartItem } from '@/api/types/cart';
import Button from '@/components/Button';
import ProductImageWithLenses from '@/components/Product/ProductImageWithLenses';
import ZeeloolCheckBox from '@/components/ZeeloolCheckBox';
import { EPrescriptionType } from '@/utils/enums/goods';
import {
  CloseOutlined,
  CollapseArrowDownOutlined,
  InfoOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import React from 'react';
import BlueLightItem from '../cartItemComponents/BlueLightItem';
import CartDetailPopup from '../cartItemComponents/CartDetailPopup';
import GiftsWithCartItem from '../cartItemComponents/GiftsWithCartItem';
import { ProductImageTags } from '../cartItemComponents/ProductImageTags';
import { env } from '@/utils/env';

interface CartItemPCProps {
  cartItemData: ICartItem;
  batchFlag: boolean;
  selected?: boolean;
  itemDisabled?: boolean; // 商品是否禁用
  itemPromotion?: any;
  currency?: any;
  itemType?: number;
  // 赠品相关属性
  giftCountDown?: number; // 赠品倒计时时间
  giftAllowType?: number; // 赠品是否可结算 1可结算 giftCountDown为可计算结束时间倒计时  2不可结算 giftCountDown为可计算开始时间倒计时
  itemGiftInfo?: any; // 独立赠品的intro提示

  // 从hooks传来的状态和方法
  loading: boolean;
  showCartDetailPopup: boolean;
  showChangeColor: boolean;
  outStock: boolean;
  lensCanotUse: boolean;
  flag1: boolean;
  flag2: boolean;
  isGlassesGrayImage: boolean;

  // 方法
  formatPrice: (price: number | string) => string;
  getProductImage: () => string;
  getProductName: () => string;
  getAllPrices: () => any;
  getPrescriptionPrice: () => { price: number; sell_price: number };
  getItemTotalPrice: () => number;
  showDownPrice: () => boolean;
  getDownPrice: () => number;
  qtyChange: (item: any, type: 'reduce' | 'add') => Promise<void>;
  handleDelete: () => void;
  deleteItem: (cartItem: ICartItem) => void;
  handleMoveToWishlist: () => void;
  handleEditPrescription: () => void;
  handleAddLens: () => void;
  handleRemoveLens: () => Promise<void>;
  handleSelectChange: (checked: boolean) => void;
  getPromotionItemText: (qty: number) => string;
  canChangeColorClick: () => boolean;
  handleChangeColorClick: (event?: React.MouseEvent<HTMLElement>) => void;
  handlePrescriptionTypeClick: (event?: React.SyntheticEvent, expanded?: boolean) => void;
  handleProductImageClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;

  // 兑换相关方法和数据
  canUseRedeemCouponToRedeem: () => boolean;
  showRedemptionDialog: () => void;
  showCancelRedemptionDialog: () => void;

  // hooks提供的方法
  onReloadCart: (data?: any) => void;
}

export const CartItemPC: React.FC<CartItemPCProps> = ({
  cartItemData,
  batchFlag,
  selected = false,
  itemDisabled = false,
  itemPromotion,
  currency,
  itemType = 1,
  // 赠品相关属性
  giftCountDown = 0,
  giftAllowType = 0,
  itemGiftInfo = {},

  // hooks状态和方法
  loading,
  showCartDetailPopup,
  showChangeColor,
  outStock,
  lensCanotUse,
  flag1,
  flag2,
  isGlassesGrayImage,

  // hooks方法
  formatPrice,
  getProductImage,
  getProductName,
  getAllPrices,
  getPrescriptionPrice,
  getItemTotalPrice,
  showDownPrice,
  getDownPrice,
  qtyChange,
  handleDelete,
  deleteItem,
  handleMoveToWishlist,
  handleEditPrescription,
  handleAddLens,
  handleRemoveLens,
  handleSelectChange,
  getPromotionItemText,
  canChangeColorClick,
  handleChangeColorClick,
  handlePrescriptionTypeClick,
  handleProductImageClick,
  // 兑换相关方法和数据
  canUseRedeemCouponToRedeem,
  showRedemptionDialog,
  showCancelRedemptionDialog,
  onReloadCart,
}) => {
  const t = useTranslations('cart.cartItem');
  // 获取产品图片的className
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const getProductImageClassName = () => {
    let widthClass = '!w-full';
    if (cartItemData.product.is_glass === 1) {
      widthClass = '!w-[80%]'; // PC端80%
    }
    const blendClass = isGlassesGrayImage ? 'mix-blend-normal' : 'mix-blend-multiply';
    return `${widthClass} object-cover ${blendClass}`;
  };

  // 渲染数量显示
  const renderQuantityDisplay = () => {
    // 获取当前数量和相关数据
    const currentQty = selected && itemPromotion ? itemPromotion.qty : cartItemData.qty;
    const currentItem = selected && itemPromotion ? itemPromotion : cartItemData;
    const isOverStock = currentQty > cartItemData.stock;

    // 数量按钮的通用样式
    const quantityButtonSx = {
      border: 'none',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&.Mui-disabled': {
        border: 'none',
        backgroundColor: 'transparent',
        opacity: 0.5,
      },
    };

    // 渲染展开状态的数量控制
    const renderUnfoldedQuantity = () => (
      <div className="flex items-center w-[75px] h-[25px] leading-[25px] md:w-[90px] md:h-[30px] md:leading-[30px] text-center">
        <Button
          disabled={loading || currentQty <= 1 || batchFlag}
          onClick={() => qtyChange(currentItem, 'reduce')}
          className={`!w-[25px] !h-[25px] !leading-[25px] md:!w-[30px] md:!h-[30px] md:!leading-[30px] !p-0 !min-w-0 ${batchFlag ? '!cursor-not-allowed' : ''}`}
          sx={quantityButtonSx}
        >
          <MinusOutlined className="text-[16px] !text-primary-black" />
        </Button>
        <span
          className={`flex-1 text-[12px] md:text-[16px] bg-[#f6f6f6] rounded-[6px] text-center ${isOverStock ? 'text-[#bc1b1b]' : ''}`}
        >
          {currentQty}
        </span>
        <Button
          disabled={loading || currentQty >= 5 || currentQty >= cartItemData.stock || batchFlag}
          onClick={() => qtyChange(currentItem, 'add')}
          className={`!w-[25px] !h-[25px] !leading-[25px] md:!w-[30px] md:!h-[30px] md:!leading-[30px] !p-0 !min-w-0 ${batchFlag ? '!cursor-not-allowed' : ''}`}
          sx={quantityButtonSx}
        >
          <PlusOutlined className="text-[16px] !text-primary-black" />
        </Button>
      </div>
    );

    // 渲染单个数量显示
    const renderSingleQuantity = () => (
      <div className="text-[16px] ml-[12px] text-primary-black">{currentQty}</div>
    );

    if (currentQty < 2) {
      return renderSingleQuantity();
    }
    return renderUnfoldedQuantity();
  };

  return (
    <div className={`pt-[16px] pr-[40px] pl-[16px] pb-[24px] bg-white`}>
      <div className={`relative flex justify-between items-center pb-[8px]`}>
        <div className="flex">
          {/* 复选框 */}
          <div className="relative bottom-[2px]">
            <ZeeloolCheckBox
              checked={selected}
              disabled={itemDisabled}
              onChange={(e) => handleSelectChange(e.target.checked)}
              fontSize={20}
            />
          </div>
          <div className="pl-[20px]">
            {/* 产品图片上方标签 - PC端 */}
            <ProductImageTags
              item={cartItemData}
              itemType={itemType}
              selected={selected}
              itemPromotion={itemPromotion}
              outStock={outStock}
              flag1={flag1}
              flag2={flag2}
              isPc
              overlayMode={false}
              giftCountDown={giftCountDown}
              giftAllowType={giftAllowType}
              itemGiftInfo={itemGiftInfo}
            />
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex justify-end items-center space-x-4">
          {[1, 4].includes(itemType) && (
            <>
              <button
                className="text-[14px] text-[#808080] underline mr-[16px] cursor-pointer"
                onClick={handleMoveToWishlist}
              >
                {cartItemData.product.is_wish === 1 ? t('removeFromWishlist') : t('moveToWishlist')}
              </button>
              <button
                className="text-[14px] text-[#808080] underline cursor-pointer"
                onClick={batchFlag ? () => deleteItem(cartItemData) : handleDelete}
              >
                {t('delete')}
              </button>
            </>
          )}

          {/* 兑换品取消兑换按钮 */}
          {itemType === 5 && (
            <button
              className={`text-[14px] text-[#808080] underline ${batchFlag ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={(e) => {
                e.preventDefault(); // 阻止默认行为
                e.stopPropagation(); // 阻止事件冒泡
                e.nativeEvent.stopImmediatePropagation(); // 完全阻止事件传播
                if (!batchFlag) {
                  showCancelRedemptionDialog();
                }
              }}
              disabled={batchFlag}
            >
              {t('cancelRedemption')}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-start space-x-4 pl-[40px]">
        {/* 镜框图片 */}
        <Link
          href={`/goods-detail/${cartItemData.product?.shop_sku}`}
          className={`relative w-[180px] h-[180px] 2xl:w-[220px] 2xl:h-[220px] bg-[#eeeeee] flex items-center justify-center overflow-hidden rounded-[6px] ${batchFlag ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={handleProductImageClick}
        >
          <ProductImageWithLenses
            imgUrl={getProductImage()}
            product={cartItemData.product || {}}
            lens={cartItemData.lens || {}}
            className={getProductImageClassName()}
          />
          {/* 产品图片覆盖标签 - PC端 */}
          <ProductImageTags
            item={cartItemData}
            itemType={itemType}
            selected={selected}
            itemPromotion={itemPromotion}
            outStock={outStock}
            flag1={flag1}
            flag2={flag2}
            isPc
            overlayMode
            giftCountDown={giftCountDown}
            giftAllowType={giftAllowType}
            itemGiftInfo={itemGiftInfo}
          />

          {/* 可兑换覆盖标签 - 仿照Vue项目的redeemed-wrap */}
          {canUseRedeemCouponToRedeem() && (
            <div
              className={`absolute bottom-0 left-0 right-0 h-[54px] text-[#463ae8] text-center flex items-center justify-center ${batchFlag ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{
                background: 'linear-gradient(180deg, rgba(235, 235, 255, 0), #ebebff)',
              }}
              onClick={(e) => {
                e.preventDefault(); // 阻止Link的默认跳转行为
                e.stopPropagation(); // 阻止事件冒泡
                e.nativeEvent.stopImmediatePropagation(); // 完全阻止事件传播
                if (!batchFlag) {
                  showRedemptionDialog();
                }
              }}
            >
              <span className="block text-[14px] border border-[#463ae8] rounded-[4px] w-[136px] h-[29px] leading-[29px] text-center">
                {t('redeemedItem')}
              </span>
            </div>
          )}
        </Link>
        <div className="flex-1 min-w-0 flex flex-col ml-[40px] 2xl:ml-[70px] min-h-[180px] 2xl:min-h-[220px]">
          {/* 商品信息 */}
          <div className={`flex-1 space-y-5 pt-[20px] flex flex-col`}>
            {/* 镜框名称 */}
            <div
              className={`mb-[7px] 2xl:mb-[12px] ${itemDisabled && itemType === 4 ? 'opacity-50' : ''}`}
            >
              <span className="font-bb text-[14px] md:text-[16px]">
                {cartItemData.product.is_glass === 1 ? t('frame2') : t('product')}
              </span>
              <span className="ml-[12px] text-[14px] md:text-[16px]">{getProductName()}</span>
            </div>

            {/* 颜色和价格 */}
            <div
              className={`flex items-center justify-between mb-[7px] 2xl:mb-[12px] ${itemDisabled && itemType === 4 ? 'opacity-50' : ''}`}
            >
              {/* 颜色区域 */}
              {canChangeColorClick() && !batchFlag ? (
                <Button
                  className="flex items-center !p-[0] !text-primary-black font-bs w-fit"
                  onClick={handleChangeColorClick}
                  sx={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <span className="font-bb text-[14px] md:text-[16px]">{t('color')}:</span>
                  <span className="ml-[12px] text-[14px] md:text-[16px]">
                    {cartItemData.product.color}
                  </span>
                  <CollapseArrowDownOutlined
                    className={`ml-[4px] text-[16px] text-[#808080] transition-transform ${showChangeColor ? 'rotate-180' : ''}`}
                  />
                </Button>
              ) : (
                <div
                  className={`flex items-center text-primary-black h-[28px] leading-[28px]`}
                  aria-label={`${t('color')}: ${cartItemData.product.color}`}
                >
                  <span className="font-bb text-[14px] md:text-[16px]">{t('color')}:</span>
                  <span className="ml-[12px] text-[14px] md:text-[16px]">
                    {cartItemData.product.color}
                  </span>
                </div>
              )}

              {/* 价格区域 */}
              {!lensCanotUse && (
                <div className="">
                  <div className="">
                    {(() => {
                      const { product: productPrice } = getAllPrices();
                      const hasDiscount =
                        productPrice.price !== productPrice.sell_price && productPrice.price > 0;

                      return (
                        <div className="flex items-end text-[14px] md:text-[16px]">
                          {hasDiscount && (
                            <span className="text-[#a9a9a9] line-through">
                              <del
                                aria-label={`${tPriceA11y('srOriginal')}${formatPrice(productPrice.price)}`}
                              >
                                {formatPrice(productPrice.price)}
                              </del>
                            </span>
                          )}
                          <span
                            className="font-bb ml-[5px]"
                            aria-label={`${tPriceA11y('srCurrent')}${formatPrice(productPrice.sell_price)}`}
                          >
                            {formatPrice(productPrice.sell_price)}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* 添加镜片 */}
            {!lensCanotUse &&
              cartItemData.option.prescription_type === EPrescriptionType.FRAME_ONLY &&
              cartItemData.product.product_lens === 1 &&
              cartItemData.product.is_glass === 1 &&
              cartItemData.product.is_show_add_lense === 1 && (
                <Button
                  disabled={batchFlag}
                  className="flex items-center bg-[#ecebfd] !text-primary-black !text-[14px]"
                  onClick={handleAddLens}
                  sx={{
                    border: 'none',
                    backgroundColor: '#ecebfd',
                    borderRadius: '6px',
                    height: '32px',
                    padding: '0',
                    marginBottom: '20px',
                    '&.Mui-disabled': {
                      backgroundColor: '#ecebfd',
                      opacity: 0.5,
                    },
                    ...(env.NEXT_PUBLIC_SITE_NAME === 'fr'
                      ? { width: '180px' }
                      : { width: '140px' }),
                  }}
                >
                  {t('selectLenses')}
                  <PlusOutlined className="ml-[2px] text-[12px]" />
                </Button>
              )}

            {/* 处方类型 */}
            {!lensCanotUse &&
              cartItemData.product.is_glass === 1 &&
              cartItemData.option.prescription_type &&
              cartItemData.option.prescription_type !== EPrescriptionType.FRAME_ONLY && (
                <div className={`w-full`}>
                  {/* 自定义折叠头部 */}
                  <div
                    className={`flex items-center justify-between mb-[12px]`}
                    onClick={batchFlag ? undefined : () => handlePrescriptionTypeClick()}
                  >
                    <div
                      className={`flex items-center ${batchFlag ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    >
                      <span className="font-bb text-[14px] md:text-[16px]">
                        {t('prescription')}:
                      </span>
                      <span className="block ml-[12px] text-[14px] md:text-[16px] max-w-[190px] overflow-hidden text-ellipsis whitespace-nowrap">
                        {cartItemData.option.prescription_type_name}
                      </span>
                      <CollapseArrowDownOutlined
                        className={`ml-[8px] text-[16px] text-[#808080] transition-transform duration-200 ${
                          showCartDetailPopup ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                    {/* 处方价格 - 只在折叠时显示 */}
                    {!showCartDetailPopup && (
                      <div className="text-right">
                        <span className="text-[14px] md:text-[16px] font-bb">
                          {formatPrice(getPrescriptionPrice().sell_price)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* 折叠内容 */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      showCartDetailPopup
                        ? 'max-h-[800px] opacity-100 mt-[16px] transform translate-y-0'
                        : 'max-h-0 opacity-0 mt-0 transform -translate-y-1'
                    }`}
                    style={{
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <div>
                      <CartDetailPopup
                        open
                        onClose={() => {}}
                        cartItemData={cartItemData}
                        selected={selected}
                        itemPromotion={itemPromotion}
                        itemType={itemType}
                        currency={currency}
                        handleEditPrescription={handleEditPrescription}
                        handleRemoveLens={handleRemoveLens}
                        isInline
                      />
                    </div>
                  </div>
                </div>
              )}

            {/* 镜片不匹配处理 */}
            {lensCanotUse && (
              <div>
                {cartItemData.lens?.is_match === 2 && (
                  <div>
                    <Button
                      disabled={batchFlag}
                      onClick={handleRemoveLens}
                      className={`flex items-center !text-[14px] !px-[7px] !h-[32px] !text-primary-black !w-fit`}
                      sx={{
                        border: 'none',
                        backgroundColor: '#F6F6F6',
                        '&.Mui-disabled': {
                          opacity: 0.5,
                        },
                      }}
                    >
                      <span className="text-secondary-black">{t('mismatchedLenses')}</span>
                      <CloseOutlined />
                    </Button>
                  </div>
                )}
                {cartItemData.lens?.is_match === 3 && (
                  <div>
                    <Button
                      disabled={batchFlag}
                      className="!text-[14px] !px-[7px] !h-[32px] !text-[#bc1b1b] !w-fit"
                      onClick={handleAddLens}
                      sx={{
                        border: 'none',
                        backgroundColor: '#f8e8e8',
                        '&.Mui-disabled': {
                          opacity: 0.5,
                        },
                      }}
                    >
                      <InfoOutlined />
                      <span className="mx-[4px]">{t('prescriptionErrors')}</span>
                      <CollapseArrowDownOutlined className="text-[15px]" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 底部数量和小计 */}
          {!lensCanotUse && (
            <div
              className={`mt-auto space-y-4 ${itemDisabled && itemType === 4 ? 'opacity-50' : ''}`}
            >
              {/* 数量 - 使用 role="group" 创建语义区域 */}
              <div
                role="group"
                className="flex items-center justify-between mt-[15px]"
                aria-label={`${t('qty')}: ${selected && itemPromotion ? itemPromotion.qty : cartItemData.qty}`}
              >
                <span className="font-bb text-[14px] md:text-[16px]">{t('qty')}</span>
                {renderQuantityDisplay()}
              </div>

              {/* 促销相关提示 - 只包含折扣相关，价格优惠提示在小计后面单独显示 */}
              {!lensCanotUse &&
                (() => {
                  // 提取公共条件变量
                  const isOutOfStock =
                    cartItemData.qty > cartItemData.stock &&
                    itemType !== 4 &&
                    cartItemData.stock > 0;
                  const hasValidPromotion =
                    selected &&
                    itemPromotion &&
                    itemPromotion.products_promotions_show === 1 &&
                    itemPromotion.over_limit_rule === 1 &&
                    itemType === 1;
                  const hasAvailableDiscount =
                    hasValidPromotion &&
                    itemPromotion.products_promotions_qty > 0 &&
                    cartItemData.qty !== itemPromotion.products_promotions_qty;
                  const isDiscountExhausted =
                    hasValidPromotion && itemPromotion.products_promotions_qty <= 0;

                  // 按优先级渲染提示（早期返回）
                  if (isOutOfStock) {
                    return (
                      <div className="text-[14px] text-[#bc1b1b] mt-[16px] text-right" role="alert">
                        {t('lowInventoryModify', { stock: cartItemData.stock })}
                      </div>
                    );
                  }

                  // 第一优先级：有折扣商品的促销提示
                  if (hasAvailableDiscount) {
                    return (
                      <div className="text-[14px] text-[#b06814] mt-[16px] text-right" role="note">
                        {t('include')} {itemPromotion.products_promotions_qty} {t('discounted')}{' '}
                        {getPromotionItemText(itemPromotion.products_promotions_qty)}.
                      </div>
                    );
                  }

                  // 第二优先级：折扣已用完提示
                  if (isDiscountExhausted) {
                    return (
                      <div className="text-[14px] mt-[16px] text-right" role="note">
                        {t('discountUsedUp')}
                      </div>
                    );
                  }

                  return null;
                })()}

              {/* 小计 */}
              <div
                role="group"
                className="flex items-center justify-between pt-[15px] mt-[15px] border-t border-[#eeeeee]"
                aria-label={`${t('subtotal')}: ${
                  selected && itemPromotion
                    ? formatPrice(
                        Number(itemPromotion.product_payable || 0) +
                          Number(itemPromotion.product_rule_discounts_price || 0),
                      )
                    : formatPrice(getItemTotalPrice())
                }`}
              >
                <span className="font-bb text-[14px] md:text-[16px]">{t('subtotal')}</span>
                {/* 价格 */}
                <div className={`text-[20px] font-bb`}>
                  {selected && itemPromotion
                    ? formatPrice(
                        Number(itemPromotion.product_payable || 0) +
                          Number(itemPromotion.product_rule_discounts_price || 0),
                      )
                    : formatPrice(getItemTotalPrice())}
                </div>
              </div>

              {/* 价格优惠提示 - 在小计后面 */}
              {showDownPrice() && itemType === 1 && (
                <div
                  className="text-[14px] mt-[0] text-right text-[#b06814]"
                  role="note"
                  aria-label={`${formatPrice(getDownPrice())} ${t('cheaperThanAdded')}`}
                >
                  {formatPrice(getDownPrice())} {t('cheaperThanAdded')}
                </div>
              )}
            </div>
          )}

          {/* 赠品组件 */}
          <GiftsWithCartItem
            className="mt-[17px]"
            cartItemData={cartItemData}
            promotion={itemPromotion ? [{ id: cartItemData.cart_id, ...itemPromotion }] : []}
          />
        </div>
      </div>
      {/* 防蓝光镜片升级组件 - 只在有new_lens或已升级时显示 */}
      {((cartItemData.lens as any)?.new_lens || (cartItemData.option as any)?.old_lens_id) &&
        selected && <BlueLightItem cartItemData={cartItemData} onReloadCart={onReloadCart} />}
    </div>
  );
};
